import React, { useMemo, useState } from 'react';
import AutoSaveIndicator from '../../../../composants/AutoSaveIndicator/AutoSaveIndicator';
import type { AutoSaveStatus } from '../../../../hooks/useAutoSave';
import ArticleDetailsModal from './ArticleDetailsModal';
import { formatPrice } from '../../../../utils/priceUtils';
import { ReactComponent as ArrowDownIcon } from '../../../../assets/icons/arrow-down.svg';
import Switch from '../../../../composants/Switch/Switch';

// ── Types ─────────────────────────────────────────────────────────────────────

export interface Article {
  id: number;
  nom: string;
  description?: string;
  seuil_a?: number;
  seuil_b?: number;
  unite: string;
}

export interface PrixLigne {
  prix_a: string;
  prix_b: string;
  prix_c: string;
}

interface SelectedLot {
  id: number;
  departement?: { id: number; nom: string };
}

export interface RegionGroup {
  regionId: number;
  regionNom: string;
  lots: SelectedLot[];
}

// ── Table articles ────────────────────────────────────────────────────────────

function ArticlesTable({
  articles,
  prix,
  disabled,
  onChangePrix,
  onVoirDetails,
}: {
  articles: Article[];
  prix: Record<number, PrixLigne>;
  disabled: boolean;
  onChangePrix: (articleId: number, champ: keyof PrixLigne, val: string) => void;
  onVoirDetails: (article: Article) => void;
}) {
  return (
    <div className="articles-table-wrapper">
      <table className="articles-table">
        <thead>
          <tr>
            <th>Ref</th>
            <th>Article</th>
            <th>Description</th>
            <th>Seuil A</th>
            <th>Seuil B</th>
            <th>Unité</th>
            <th>Prix A<br /><small>&lt; seuil A (€)</small></th>
            <th>Prix B<br /><small>entre A et B (€)</small></th>
            <th>Prix C<br /><small>&gt; seuil B (€)</small></th>
          </tr>
        </thead>
        <tbody>
          {articles.map((article, i) => {
            const isIncomplete = !prix[article.id]?.prix_a || !prix[article.id]?.prix_b || !prix[article.id]?.prix_c;
            return (
              <tr key={article.id} className={isIncomplete ? 'article-row--incomplete' : ''}>
                <td>{i + 1}</td>
                <td>{article.nom}</td>
                <td>
                  <button
                    type="button"
                    className="btn-voir-details"
                    onClick={() => onVoirDetails(article)}
                  >
                    Voir les détails
                  </button>
                </td>
                <td>{article.seuil_a ?? '—'}</td>
                <td>{article.seuil_b ?? '—'}</td>
                <td>{article.unite}</td>
                <td>
                  <div className="prix-input-cell">
                    <input
                      type="number"
                      className="prix-input"
                      min="0"
                      step="0.01"
                      value={prix[article.id]?.prix_a ?? ''}
                      disabled={disabled}
                      onChange={(e) => onChangePrix(article.id, 'prix_a', e.target.value)}
                    />
                    <span className="prix-input-cell__suffix">€</span>
                  </div>
                </td>
                <td>
                  <div className="prix-input-cell">
                    <input
                      type="number"
                      className="prix-input"
                      min="0"
                      step="0.01"
                      value={prix[article.id]?.prix_b ?? ''}
                      disabled={disabled}
                      onChange={(e) => onChangePrix(article.id, 'prix_b', e.target.value)}
                    />
                    <span className="prix-input-cell__suffix">€</span>
                  </div>
                </td>
                <td>
                  <div className="prix-input-cell">
                    <input
                      type="number"
                      className="prix-input"
                      min="0"
                      step="0.01"
                      value={prix[article.id]?.prix_c ?? ''}
                      disabled={disabled}
                      onChange={(e) => onChangePrix(article.id, 'prix_c', e.target.value)}
                    />
                    <span className="prix-input-cell__suffix">€</span>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ── Accordéon (avec badge de complétion — Story 13.6) ──────────────────────

function Accordion({
  title,
  badge,
  children,
}: {
  title: string;
  badge?: string;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(false);
  return (
    <div className="accordion">
      <button className="accordion-header" onClick={() => setOpen((o) => !o)}>
        <span>{title}</span>
        {badge && <span className="accordion-badge">{badge}</span>}
        <ArrowDownIcon className={`accordion-chevron${open ? ' accordion-chevron--open' : ''}`} />
      </button>
      {open && <div className="accordion-body">{children}</div>}
    </div>
  );
}

// ── Props ─────────────────────────────────────────────────────────────────────

interface SectionOffreFinanciereProps {
  poidsOffreFinanciere: number;
  articles: Article[];
  canEdit: boolean;
  isRegional: boolean;
  regionGroups: RegionGroup[];
  prixIdentiques: boolean;
  prixLignes: Record<number, PrixLigne>;
  prixParLot: Record<number, Record<number, PrixLigne>>;
  prixParRegion: Record<number, Record<number, PrixLigne>>;
  selectedLots: SelectedLot[];
  onTogglePrixIdentiques: () => void;
  onChangePrix: (articleId: number, champ: keyof PrixLigne, val: string) => void;
  onChangePrixLot: (lotId: number, articleId: number, champ: keyof PrixLigne, val: string) => void;
  onChangePrixRegion: (regionId: number, articleId: number, champ: keyof PrixLigne, val: string) => void;
  autoSaveStatus: AutoSaveStatus;
  autoSaveError: string | null;
}

// ── Section "Offre financière" ──────────────────────────────────────────────

const SectionOffreFinanciere: React.FC<SectionOffreFinanciereProps> = ({
  poidsOffreFinanciere,
  articles,
  canEdit,
  isRegional,
  regionGroups,
  prixIdentiques,
  prixLignes,
  prixParLot,
  prixParRegion,
  selectedLots,
  onTogglePrixIdentiques,
  onChangePrix,
  onChangePrixLot,
  onChangePrixRegion,
  autoSaveStatus,
  autoSaveError,
}) => {
  // Label du toggle adapté au type de lot
  const toggleLabel = isRegional
    ? 'Mes prix seront les mêmes pour chacune des régions'
    : 'Mes prix seront les mêmes pour chaque lot géographique';

  // Calcul du total HT et de la commission 5%
  const { totalHT, commission } = useMemo(() => {
    let total = 0;
    const sumPrix = (p: PrixLigne) => {
      total += parseFloat(p.prix_a) || 0;
      total += parseFloat(p.prix_b) || 0;
      total += parseFloat(p.prix_c) || 0;
    };

    if (prixIdentiques) {
      Object.values(prixLignes).forEach(sumPrix);
    } else if (isRegional) {
      Object.values(prixParRegion).forEach((regionPrix) =>
        Object.values(regionPrix).forEach(sumPrix)
      );
    } else {
      Object.values(prixParLot).forEach((lotPrix) =>
        Object.values(lotPrix).forEach(sumPrix)
      );
    }

    return { totalHT: total, commission: total * 0.05 };
  }, [prixIdentiques, prixLignes, prixParRegion, prixParLot, isRegional]);

  // ── Détection prix manquants (Story 13.6) ──────────────────────────────────

  const completionInfo = useMemo(() => {
    const isComplete = (p: PrixLigne | undefined) =>
      p != null && p.prix_a !== '' && p.prix_b !== '' && p.prix_c !== '';

    const totalArticles = articles.length;

    if (prixIdentiques) {
      const nbComplets = articles.filter((a) => isComplete(prixLignes[a.id])).length;
      return { total: totalArticles, complets: nbComplets, manquants: totalArticles - nbComplets, parGroupe: null };
    }

    if (isRegional) {
      const parGroupe = regionGroups.map(({ regionId, regionNom }) => {
        const regPrix = prixParRegion[regionId] ?? {};
        const nbComplets = articles.filter((a) => isComplete(regPrix[a.id])).length;
        return { id: regionId, nom: regionNom, total: totalArticles, complets: nbComplets };
      });
      const totalAll = parGroupe.reduce((s, g) => s + g.total, 0);
      const completsAll = parGroupe.reduce((s, g) => s + g.complets, 0);
      return { total: totalAll, complets: completsAll, manquants: totalAll - completsAll, parGroupe };
    }

    // Lot 2xx
    const parGroupe = selectedLots.map((lot) => {
      const lotPrix = prixParLot[lot.id] ?? {};
      const nbComplets = articles.filter((a) => isComplete(lotPrix[a.id])).length;
      return { id: lot.id, nom: lot.departement?.nom ?? `Lot ${lot.id}`, total: totalArticles, complets: nbComplets };
    });
    const totalAll = parGroupe.reduce((s, g) => s + g.total, 0);
    const completsAll = parGroupe.reduce((s, g) => s + g.complets, 0);
    return { total: totalAll, complets: completsAll, manquants: totalAll - completsAll, parGroupe };
  }, [articles, prixIdentiques, prixLignes, isRegional, regionGroups, prixParRegion, selectedLots, prixParLot]);

  // Badge de complétion pour un accordéon
  const getCompletionBadge = (groupId: number): string | undefined => {
    const groupInfo = completionInfo.parGroupe?.find((g) => g.id === groupId);
    if (!groupInfo) return undefined;
    if (groupInfo.complets === groupInfo.total) return `${groupInfo.complets}/${groupInfo.total} ✅`;
    if (groupInfo.complets === 0) return `0/${groupInfo.total} ❌`;
    return `${groupInfo.complets}/${groupInfo.total} ⚠️`;
  };

  // État modal détails article
  const [detailArticle, setDetailArticle] = useState<Article | null>(null);

  const handleVoirDetails = (article: Article) => setDetailArticle(article);
  const handleCloseDetails = () => setDetailArticle(null);

  // Rendu du contenu "prix différents" selon le type de lot
  const renderPrixDifferents = () => {
    if (isRegional) {
      if (regionGroups.length === 0) {
        return (
          <p className="section-empty">
            Sélectionnez d'abord des régions dans la section "Choix des lots".
          </p>
        );
      }
      return (
        <div className="accordeons-list">
          {regionGroups.map(({ regionId, regionNom }) => (
            <Accordion key={regionId} title={regionNom} badge={getCompletionBadge(regionId)}>
              <ArticlesTable
                articles={articles}
                prix={prixParRegion[regionId] ?? {}}
                disabled={!canEdit}
                onChangePrix={(articleId, champ, val) =>
                  onChangePrixRegion(regionId, articleId, champ, val)
                }
                onVoirDetails={handleVoirDetails}
              />
            </Accordion>
          ))}
        </div>
      );
    }

    if (selectedLots.length === 0) {
      return (
        <p className="section-empty">
          Sélectionnez d'abord les lots géographiques dans la section "Choix des lots".
        </p>
      );
    }
    return (
      <div className="accordeons-list">
        {selectedLots.map((lot) => (
          <Accordion key={lot.id} title={lot.departement?.nom ?? ''} badge={getCompletionBadge(lot.id)}>
            <ArticlesTable
              articles={articles}
              prix={prixParLot[lot.id] ?? {}}
              disabled={!canEdit}
              onChangePrix={(articleId, champ, val) =>
                onChangePrixLot(lot.id, articleId, champ, val)
              }
              onVoirDetails={handleVoirDetails}
            />
          </Accordion>
        ))}
      </div>
    );
  };

  return (
    <>
      <div className="section-header">
        Offre financière (pondérée à {poidsOffreFinanciere}%)
      </div>
      <div className='section-content'>
        {/* Toggle prix identiques */}
        <div className="prix-toggle-wrapper">
          <label className="prix-toggle-label">
            {toggleLabel}
            <Switch
              id="prix-identiques-switch"
              isOn={prixIdentiques}
              handleToggle={() => onTogglePrixIdentiques()}
              onColor="#da7027"
            />
          </label>
        </div>

        {/* Compteur de complétion des prix (Story 13.6) */}
        {articles.length > 0 && (
          <div className="prix-completion-counter">
            <span>📊 Articles renseignés : {completionInfo.complets} / {completionInfo.total}</span>
            {completionInfo.manquants > 0 && (
              <>
                <span className="prix-manquants"> — {completionInfo.manquants} prix manquant(s)</span>
                <button
                  type="button"
                  className="btn-scroll-manquant"
                  onClick={() => {
                    const el = document.querySelector('.article-row--incomplete');
                    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                  }}
                >
                  Voir le premier incomplet ↓
                </button>
              </>
            )}
          </div>
        )}

        {articles.length === 0 ? (
          <p className="section-empty">Aucun article défini pour ce lot technique.</p>
        ) : prixIdentiques ? (
          <ArticlesTable
            articles={articles}
            prix={prixLignes}
            disabled={!canEdit}
            onChangePrix={onChangePrix}
            onVoirDetails={handleVoirDetails}
          />
        ) : (
          renderPrixDifferents()
        )}

        {/* Indicateur auto-save (Story 13.4) */}
        <div style={{ borderTop: '1px solid #e5e7eb', marginTop: '0.5rem', paddingTop: '0.5rem' }}>
          <AutoSaveIndicator status={autoSaveStatus} error={autoSaveError} />
        </div>

        {/* Modal détails article */}
        {detailArticle && (
          <ArticleDetailsModal
            articleNom={detailArticle.nom}
            articleDescription={detailArticle.description ?? ''}
            onClose={handleCloseDetails}
          />
        )}
      </div>
    </>
  );
};

export default SectionOffreFinanciere;
